// A date field that always reads YYYY-MM-DD (or YYYY-MM with type="month"), whatever the browser's
// locale (Chris, 2026-10-14).
//
// Typing is one run of digits: the dashes go in by themselves, so 20261015 reads 2026-10-15, and a
// month or day that can only be one digit is padded at once ("3" → "03-"). A pasted 10/15/2026 is
// read as US month/day/year. Arrow up and down move the date a day (or a month). Tab leaves the field
// as usual (Chris, 2026-10-15: segment-by-segment entry was slow).
//
// The calendar button opens Base Camp's own calendar. The browser's picker, opened from a hidden
// input, stopped opening in some browsers and inside Teams.
//
// Drop-in for Fluent's <Input type="date">: the same props, and onChange fires only with a complete,
// valid date or an empty string.

import { useEffect, useState, type ChangeEvent, type KeyboardEvent } from "react";
import { Button, Input, Popover, PopoverSurface, PopoverTrigger, type InputProps } from "@fluentui/react-components";
import { isIsoDate } from "../../core";

const CALENDAR = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2" aria-hidden="true">
    <rect x="2" y="3" width="12" height="11" rx="1.5" />
    <path d="M2 6.5h12M5 1.8v2.4M11 1.8v2.4" />
  </svg>
);

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

const valid = (text: string, month: boolean) => (month ? /^\d{4}-(0[1-9]|1[0-2])$/.test(text) : /^\d{4}-\d{2}-\d{2}$/.test(text) && isIsoDate(text));
const pad = (n: number) => String(n).padStart(2, "0");
const iso = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`;
const daysIn = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate();
const todayIso = () => {
  const t = new Date();
  return iso(t.getFullYear(), t.getMonth() + 1, t.getDate());
};

/**
 * What the field shows after a keystroke. `grew` is whether the text got longer: dashes are added only
 * while typing forward, so backspace can take one out.
 */
export function formatTyped(raw: string, month: boolean, grew: boolean): string {
  // A pasted US date: 10/15/2026, or 10/2026 for a month.
  const us = month ? /^\s*(\d{1,2})\/(\d{4})\s*$/.exec(raw) : /^\s*(\d{1,2})\/(\d{1,2})\/(\d{4})\s*$/.exec(raw);
  if (us) return month ? `${us[2]}-${pad(Number(us[1]))}` : `${us[3]}-${pad(Number(us[1]))}-${pad(Number(us[2]))}`;
  let digits = raw.replace(/\D/g, "");
  // A month starting 2–9, or a day starting 4–9, can only be that one digit.
  if (digits.length === 5 && Number(digits[4]) > 1) digits = `${digits.slice(0, 4)}0${digits[4]}`;
  if (!month && digits.length === 7 && Number(digits[6]) > 3) digits = `${digits.slice(0, 6)}0${digits[6]}`;
  digits = digits.slice(0, month ? 6 : 8);
  let out = digits.slice(0, 4);
  if (digits.length > 4 || (grew && digits.length === 4)) out += "-";
  if (digits.length > 4) out += digits.slice(4, 6);
  if (!month && (digits.length > 6 || (grew && digits.length === 6))) out += "-";
  if (!month && digits.length > 6) out += digits.slice(6, 8);
  return out;
}

/** The date `step` days (or months) from `text`, or null when `text` isn't a complete date. */
function stepped(text: string, month: boolean, step: number): string | null {
  if (!valid(text, month)) return null;
  const [y, m, d] = text.split("-").map(Number);
  if (month) {
    const t = new Date(Date.UTC(y, m - 1 + step, 1));
    return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}`;
  }
  const t = new Date(Date.UTC(y, m - 1, d + step));
  return iso(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate());
}

export function DateInput({ type, value, onChange, disabled, min, max, ...rest }: InputProps) {
  const month = type === "month";
  const [text, setText] = useState(String(value ?? ""));
  const [open, setOpen] = useState(false);
  useEffect(() => setText(String(value ?? "")), [value]);
  const bad = text !== "" && !valid(text, month);
  const lo = typeof min === "string" ? min : undefined;
  const hi = typeof max === "string" ? max : undefined;

  const commit = (ev: ChangeEvent<HTMLInputElement> | KeyboardEvent<HTMLInputElement> | null, next: string) => {
    setText(next);
    if (next === "" || valid(next, month)) onChange?.((ev ?? {}) as ChangeEvent<HTMLInputElement>, { value: next });
  };

  const onKeyDown = (ev: KeyboardEvent<HTMLInputElement>) => {
    if (ev.key === "ArrowUp" || ev.key === "ArrowDown") {
      const next = stepped(text || (month ? todayIso().slice(0, 7) : todayIso()), month, ev.key === "ArrowUp" ? 1 : -1);
      if (next && (!lo || next >= lo) && (!hi || next <= hi)) {
        ev.preventDefault();
        commit(ev, next);
      }
    }
    rest.onKeyDown?.(ev);
  };

  return (
    <span className="date-input">
      <Input
        {...rest}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        value={text}
        disabled={disabled}
        placeholder={month ? "YYYY-MM" : "YYYY-MM-DD"}
        aria-invalid={bad || undefined}
        className={[rest.className, "date-input-text", month ? "month" : ""].filter(Boolean).join(" ")}
        onChange={(ev, d) => commit(ev, formatTyped(d.value, month, d.value.length > text.length))}
        onKeyDown={onKeyDown}
        onBlur={(ev) => {
          if (bad) setText(String(value ?? "")); // an unfinished date goes back to the last good one
          rest.onBlur?.(ev);
        }}
        contentAfter={
          <Popover open={open} onOpenChange={(_, d) => setOpen(d.open)} positioning="below-end" trapFocus>
            <PopoverTrigger disableButtonEnhancement>
              <Button size="small" appearance="transparent" icon={CALENDAR} disabled={disabled} aria-label="Open calendar" />
            </PopoverTrigger>
            <PopoverSurface className="date-popover">
              <Calendar
                month={month}
                value={valid(text, month) ? text : ""}
                min={lo}
                max={hi}
                onPick={(picked) => {
                  commit(null, picked);
                  setOpen(false);
                }}
              />
            </PopoverSurface>
          </Popover>
        }
      />
    </span>
  );
}

/** A month of days (or, for type="month", a year of months) to click. */
function Calendar({ month, value, min, max, onPick }: { month: boolean; value: string; min?: string; max?: string; onPick: (iso: string) => void }) {
  const start = value || (month ? todayIso().slice(0, 7) : todayIso());
  const [y0, m0] = start.split("-").map(Number);
  const [view, setView] = useState({ y: y0, m: m0 });
  const out = (d: string) => (!!min && d < min.slice(0, d.length)) || (!!max && d > max.slice(0, d.length));
  const shift = (n: number) =>
    setView((v) => {
      if (month) return { y: v.y + n, m: v.m };
      const t = new Date(Date.UTC(v.y, v.m - 1 + n, 1));
      return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1 };
    });

  const head = (
    <div className="cal-head">
      <Button size="small" appearance="subtle" aria-label={month ? "Previous year" : "Previous month"} onClick={() => shift(-1)}>
        ‹
      </Button>
      <strong aria-live="polite">{month ? view.y : `${MONTHS[view.m - 1]} ${view.y}`}</strong>
      <Button size="small" appearance="subtle" aria-label={month ? "Next year" : "Next month"} onClick={() => shift(1)}>
        ›
      </Button>
    </div>
  );

  if (month) {
    return (
      <div className="cal">
        {head}
        <div className="cal-grid months">
          {MONTHS.map((label, i) => {
            const d = `${view.y}-${pad(i + 1)}`;
            return (
              <button key={d} type="button" className={d === value ? "cal-day picked" : "cal-day"} disabled={out(d)} onClick={() => onPick(d)}>
                {label}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Weeks start on Monday, as Base Camp's weeks do.
  const lead = (new Date(Date.UTC(view.y, view.m - 1, 1)).getUTCDay() + 6) % 7;
  const today = todayIso();
  const cells: (string | null)[] = [...Array(lead).fill(null), ...Array.from({ length: daysIn(view.y, view.m) }, (_, i) => iso(view.y, view.m, i + 1))];
  return (
    <div className="cal">
      {head}
      <div className="cal-grid">
        {WEEKDAYS.map((w) => (
          <span key={w} className="cal-weekday">
            {w}
          </span>
        ))}
        {cells.map((d, i) =>
          d ? (
            <button
              key={d}
              type="button"
              className={["cal-day", d === value ? "picked" : "", d === today ? "today" : ""].filter(Boolean).join(" ")}
              disabled={out(d)}
              aria-label={d}
              aria-pressed={d === value}
              onClick={() => onPick(d)}
            >
              {Number(d.slice(8))}
            </button>
          ) : (
            <span key={`blank-${i}`} />
          ),
        )}
      </div>
      <div className="cal-foot">
        <Button size="small" appearance="subtle" disabled={out(today)} onClick={() => onPick(today)}>
          Today
        </Button>
      </div>
    </div>
  );
}
